import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dummyUserImage } from '../../services/mock_data';
import { IMAGEPATH } from '../../services/api-helper';
import NoteText from './noteFormat';
import './aisearch.scss';

const formatDate = (d: string | undefined) =>
    d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const AiResultCard = ({ result, note, notesLoading, index = 0 }: any) => {
    const navigate = useNavigate();
    const isMember = result?.entityType === 'member';
    const profile = result?.profile;
    const organization = result?.organization;
    const name = isMember ? `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() : organization?.organizationName;
    const image = isMember
        ? (profile?.profileImage ? `${IMAGEPATH + profile.profileImage}` : dummyUserImage)
        : (organization?.logo ? `${IMAGEPATH + organization.logo}` : dummyUserImage);
    const tags: any[] = isMember ? (profile?.expertises?.tags || []) : [];
    const matched: string[] = result?.matchedTags || [];
    // tags keep their stored order; beyond the first eight they're revealed in place on demand
    const TAG_LIMIT = 8;
    const [showAllTags, setShowAllTags] = useState(false);
    const visibleTags = showAllTags ? tags : tags.slice(0, TAG_LIMIT);
    const hiddenTags = tags.length - visibleTags.length;
    const updated = formatDate(isMember ? profile?.updatedAt : organization?.updatedAt);
    const memberOrg = isMember ? profile?.user?.organization : null;
    const open = () => navigate(isMember ? `/profile/${profile?.sysName}` : `/org/${organization?.sysName}`);
    const openOrg = () => navigate(`/org/${memberOrg?.sysName}`);
    const searchTag = (skillName: string) => navigate(`/page/members?q=${encodeURIComponent(skillName)}`);

    return (
        <article className="ai-card">
            <span className={`ai-badge ${isMember ? 'member' : 'facility'}`}>{isMember ? 'Member' : 'Facility'}</span>
            <button type="button" className="ai-avatar-btn" onClick={open} aria-label={`Open ${name}`}>
                {/* facility logos are wordmarks: fit them, don't crop them */}
                <img className={`ai-avatar${isMember ? '' : ' logo'}`} src={image} alt="" />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ai-name" onClick={open}>{name}</div>
                <div className="text-muted small-font">
                    {isMember ? profile?.jobTitle : 'Organization'}
                    {memberOrg?.organizationName ? (
                        <>
                            {' | '}
                            {memberOrg.sysName
                                ? <button type="button" className="ai-orglink" onClick={openOrg}>{memberOrg.organizationName}</button>
                                : memberOrg.organizationName}
                        </>
                    ) : ''}
                </div>

                {note?.note ? (
                    <div className="ai-why" style={{ animationDelay: `${Math.min(index * 90, 450)}ms` }}>
                        <span className="ai-why-label">Why this matches</span>
                        <NoteText text={note.note} />
                    </div>
                ) : notesLoading ? (
                    <div className="ai-noteskel" aria-hidden="true"><div className="bar" /><div className="bar" /><div className="bar" /></div>
                ) : null}

                {!isMember && organization?.description && (
                    <p className="text-muted small-font mt-2 mb-0">{organization.description}</p>
                )}

                {tags.length > 0 && (
                    <div className="ai-tags">
                        {visibleTags.map(({ skillName }: any, i: number) => (
                            <button type="button" key={i} className={`tag${matched.includes(skillName) ? ' hit' : ''}`}
                                onClick={() => searchTag(skillName)} title={`Search for ${skillName}`}>{skillName}</button>
                        ))}
                        {hiddenTags > 0 && (
                            <button type="button" className="tag-more" onClick={() => setShowAllTags(true)}
                                title={`Show all ${tags.length} tags`}>+{hiddenTags} more</button>
                        )}
                    </div>
                )}

                {updated && <div className="ai-foot">Updated {updated}</div>}
            </div>
        </article>
    );
};

export default AiResultCard;
